import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IVendorAssessmentQuestionOption,
  IVendorAssessmentQuestionOptionInput,
} from "models/vendorAssessmentQuestionOption";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  VendorAssessmentQuestionOptionCells,
  addVendorAssessmentQuestionOption,
  deleteVendorAssessmentQuestionOption,
  editVendorAssessmentQuestionOption,
  getVendorAssessmentQuestionOptions,
} from "framework/vendorAssessmentQuestionOption";
import AddVendorAssessmentQuestionOption from "./partials/AddVendorAssessmentQuestionOption/AddVendorAssessmentQuestionOption";
import BlockIcon from "@mui/icons-material/Block";
import EditVendorAssessmentQuestionOption from "./partials/EditVendorAssessmentQuestionOption/EditVendorAssessmentQuestionOption";
import DeleteVendorAssessmentQuestionOption from "./partials/DeleteVendorAssessmentQuestionOption/DeleteVendorAssessmentQuestionOption";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission } from "helpers";
import { useParams } from "react-router-dom";

interface Props {
  feature: Feature;
}

const VendorAssessmentQuestionOptions: React.FC<Props> = ({ feature }) => {
  const urlParams = useParams();
  let questionId: string = urlParams[`${feature.singleName}Id`] as string;
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [VendorAssessmentQuestion, setVendorAssessmentQuestionOption] =
    useState<IVendorAssessmentQuestionOption[]>(
      [] as IVendorAssessmentQuestionOption[]
    );
  const [
    FilterVendorAssessmentQuestion,
    setFilterVendorAssessmentQuestionOption,
  ] = useState<IVendorAssessmentQuestionOption[]>(
    [] as IVendorAssessmentQuestionOption[]
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [
    selectedVendorAssessmentQuestionOption,
    setSelectedVendorAssessmentQuestionOption,
  ] = useState<IVendorAssessmentQuestionOption>(
    {} as IVendorAssessmentQuestionOption
  );
  const [inputObj, setInputObj] =
    useState<IVendorAssessmentQuestionOptionInput>(
      {} as IVendorAssessmentQuestionOptionInput
    );
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addVendorAssessmentQuestionMutation = useMutation({
    mutationFn: (createInput: IVendorAssessmentQuestionOptionInput) => {
      return addVendorAssessmentQuestionOption(createInput);
    },
  });

  const editVendorAssessmentQuestionMutation = useMutation({
    mutationFn: (data: {
      createInput: IVendorAssessmentQuestionOptionInput;
      id: string;
    }) => {
      return editVendorAssessmentQuestionOption(data.createInput, data.id);
    },
  });

  const deleteVendorAssessmentQuestionOptionMutation = useMutation({
    mutationFn: (data: { id: string }) => {
      return deleteVendorAssessmentQuestionOption(data.id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery(
        "get workshop evaluation question options",
        () =>
          getVendorAssessmentQuestionOptions(
            `/byquestion?vendorAssessmentQuestionId=${questionId}`
          )
      );
      response =
        response && response.length > 0
          ? response
              .map((item: IVendorAssessmentQuestionOption) => ({
                ...item,
              }))
              .sort((a: IVendorAssessmentQuestionOption, b: IVendorAssessmentQuestionOption) =>
                (a.optionOrder ?? 0) - (b.optionOrder ?? 0)
              )
          : [];
      setVendorAssessmentQuestionOption(response);
      setFilterVendorAssessmentQuestionOption(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(41);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (
    VendorAssessmentQuestion: IVendorAssessmentQuestionOption
  ) => {
    setSelectedVendorAssessmentQuestionOption(VendorAssessmentQuestion);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedVendorAssessmentQuestionOption(
      {} as IVendorAssessmentQuestionOption
    );
  };

  const handleOpenDelete = (
    VendorAssessmentQuestion: IVendorAssessmentQuestionOption
  ) => {
    setSelectedVendorAssessmentQuestionOption(VendorAssessmentQuestion);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedVendorAssessmentQuestionOption(
      {} as IVendorAssessmentQuestionOption
    );
  };

  const getBodyCells = (row: IVendorAssessmentQuestionOption) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "description")
        return {
          id: "description",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.description}</Typography>
            </TableCell>
          ),
        };

      if (key === "descriptionAr")
        return {
          id: "descriptionAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.descriptionAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "score")
        return {
          id: "score",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.score}</Typography>
            </TableCell>
          ),
        };

      if (key === "optionOrder")
        return {
          id: "optionOrder",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.optionOrder}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"right"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      color={"error"}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.description || !inputObj.descriptionAr || !inputObj.score)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);
      inputObj.questionId = questionId;
      await addVendorAssessmentQuestionMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "VendorAssessmentQuestion added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setInputObj({} as IVendorAssessmentQuestionOptionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: IVendorAssessmentQuestionOptionInput = {
        questionId: questionId,
        description:
          inputObj.description ||
          selectedVendorAssessmentQuestionOption.description,
        descriptionAr:
          inputObj.descriptionAr ||
          selectedVendorAssessmentQuestionOption.descriptionAr,
        score: inputObj.score || selectedVendorAssessmentQuestionOption.score,
        optionOrder: inputObj.optionOrder || selectedVendorAssessmentQuestionOption.optionOrder,
      };
      await editVendorAssessmentQuestionMutation.mutateAsync({
        createInput,
        id: selectedVendorAssessmentQuestionOption.id,
      });
      setAlert({
        open: true,
        message: "VendorAssessmentQuestion updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setInputObj({} as IVendorAssessmentQuestionOptionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteVendorAssessmentQuestionOptionMutation.mutateAsync({
        id: selectedVendorAssessmentQuestionOption.id,
      });
      setAlert({
        open: true,
        message: "VendorAssessmentQuestion deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography variant="h3" color={"header.primary"}>
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_industry")}>
                <IconButton
                  sx={{
                    // bgcolor: 'primary.main',
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! &&
          FilterVendorAssessmentQuestion.length > 0 && (
            <List
              headCells={VendorAssessmentQuestionOptionCells}
              data={FilterVendorAssessmentQuestion}
              getBodyCells={getBodyCells}
              feature={feature}
              canEdit={false}
            />
          )}
        {(!FilterVendorAssessmentQuestion ||
          FilterVendorAssessmentQuestion.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_option_header")}
              message={t("T_no_option_message")}
            />
          )}
        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddVendorAssessmentQuestionOption
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditVendorAssessmentQuestionOption
          open={openEdit}
          loading={loading2}
          vendorAssessmentQuestionOption={
            selectedVendorAssessmentQuestionOption
          }
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteVendorAssessmentQuestionOption
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default VendorAssessmentQuestionOptions;
